/**
 * @description
 * Stream status component displaying connection status and progress.
 * Provides visual feedback for streaming operations.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 8, 2025
 * @see admin/src/frontend/components/vscode-lens/StreamStatus.test.tsx
 */

import { AlertCircle, CheckCircle, Loader2, Radio } from 'lucide-react';
import type { StreamStatus as StreamStatusType } from '../../../types/vscode-lens';

interface StreamStatusProps {
  status: StreamStatusType['status'];
  recordsStreamed?: number;
  totalRecords?: number;
  percentComplete?: number;
  error?: string;
}

/**
 * Stream status component
 */
export function StreamStatus({
  status,
  recordsStreamed = 0,
  totalRecords = 0,
  percentComplete = 0,
  error,
}: StreamStatusProps) {
  const getStatusIcon = () => {
    switch (status) {
      case 'idle':
        return <Radio className="h-4 w-4 text-muted-foreground" />;
      case 'connecting':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'streaming':
        return <Radio className="h-4 w-4 text-green-500 animate-pulse" />;
      case 'complete':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'idle':
        return 'Ready';
      case 'connecting':
        return 'Connecting...';
      case 'streaming':
        return 'Streaming';
      case 'complete':
        return 'Complete';
      case 'error':
        return 'Error';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'idle':
        return 'text-muted-foreground';
      case 'connecting':
        return 'text-blue-500';
      case 'streaming':
        return 'text-green-500';
      case 'complete':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className={`text-sm font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      {status === 'streaming' && (
        <div className="mb-3">
          <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${percentComplete}%` }}
            />
          </div>
          <div className="mt-1 text-xs text-muted-foreground text-right">
            {(percentComplete || 0).toFixed(1)}%
          </div>
        </div>
      )}

      {/* Complete Progress Bar */}
      {status === 'complete' && (
        <div className="mb-3">
          <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-green-500 w-full" />
          </div>
          <div className="mt-1 text-xs text-green-600 text-right">
            100%
          </div>
        </div>
      )}

      {/* Error Message */}
      {status === 'error' && error && (
        <div className="mt-2 rounded-md bg-red-50 dark:bg-red-950/20 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      {(status === 'streaming' || status === 'complete') && recordsStreamed > 0 && (
        <div className="grid grid-cols-3 gap-4 pt-3 border-t">
          <div>
            <div className="text-xs text-muted-foreground">Streamed</div>
            <div className="text-lg font-semibold">
              {(recordsStreamed || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="text-lg font-semibold">
              {(totalRecords || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Progress</div>
            <div className="text-lg font-semibold">
              {(percentComplete || 0).toFixed(0)}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */